import React, { useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    Input,
    Popconfirm,
    Row,
    Select,
    Space,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SearchOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { resolveCriterionText } from "../../utils/criterionTranslations";
import {
    RequestAddCriterion,
    RequestCreateTemplate,
    RequestDeleteTemplate,
    RequestFetchTemplates,
    RequestRemoveCriterion,
    RequestUpdateTemplate,
    setEditingTemplate,
    setFilters,
    setNewTemplateName,
    setSearchTerm,
    setSelectedTemplate,
} from "../../Actions/templateAction";
import "./TemplateCritere.css";

const { Text } = Typography;
const { Option } = Select;

function TemplateCritere({ criteria = [], creators = [] }) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation("criteriaView");
    const language = i18n.resolvedLanguage || i18n.language;
    const [isCreating, setIsCreating] = useState(false);

    const {
        templates,
        selectedTemplateId,
        searchTerm,
        filters,
        editingTemplate,
        newTemplateName,
    } = useSelector((state) => state.template);

    useEffect(() => {
        dispatch(RequestFetchTemplates());
    }, [dispatch]);

    useEffect(() => {
        if (templates.length > 0 && !selectedTemplateId) {
            dispatch(setSelectedTemplate(templates[0].id));
        }
    }, [templates, selectedTemplateId, dispatch]);

    const selectedTemplate = useMemo(
        () => templates.find((tpl) => tpl.id === selectedTemplateId),
        [templates, selectedTemplateId]
    );

    const selectedCriteria = useMemo(() => {
        if (!selectedTemplate?.criteria) return [];
        return selectedTemplate.criteria.filter(
            (criterion, index, self) =>
                index === self.findIndex((item) => item.id === criterion.id)
        );
    }, [selectedTemplate?.criteria]);

    const selectedIds = useMemo(
        () => new Set(selectedCriteria.map((criterion) => criterion.id)),
        [selectedCriteria]
    );

    const sections = useMemo(
        () => [...new Set(criteria.map((criterion) => criterion.section))].filter(Boolean),
        [criteria]
    );

    const creatorOptions = useMemo(() => {
        const source = creators.length
            ? creators
            : criteria.map((criterion) => criterion.creator).filter(Boolean);

        return source.reduce((acc, creator) => {
            if (!creator?.id || acc.some((item) => item.id === creator.id)) return acc;
            acc.push(creator);
            return acc;
        }, []);
    }, [creators, criteria]);

    const availableCriteria = useMemo(() => {
        const value = searchTerm.trim().toLowerCase();

        return criteria.filter((criterion) => {
            const text = resolveCriterionText(criterion, language);
            const matchesSearch =
                !value ||
                text.title?.toLowerCase().includes(value) ||
                criterion.section?.toLowerCase().includes(value) ||
                text.description?.toLowerCase().includes(value);
            const matchesSection = filters.section
                ? criterion.section === filters.section
                : true;
            const matchesCreator = filters.creator
                ? String(criterion.creator?.id) === String(filters.creator)
                : true;
            const matchesActive =
                filters.is_active !== null ? criterion.is_active === filters.is_active : true;

            return (
                matchesSearch &&
                matchesSection &&
                matchesCreator &&
                matchesActive &&
                !selectedIds.has(criterion.id)
            );
        });
    }, [criteria, filters, searchTerm, selectedIds, language]);

    const templateNameExists = useMemo(() => {
        return templates.some(
            (tpl) => tpl.title.toLowerCase() === newTemplateName?.trim().toLowerCase()
        );
    }, [templates, newTemplateName]);

    const handleCreateTemplate = () => {
        if (!newTemplateName.trim()) {
            message.error(t("template.errors.emptyName"));
            return;
        }
        if (templateNameExists) {
            message.error(t("template.errors.duplicateName"));
            return;
        }

        setIsCreating(true);
        dispatch(RequestCreateTemplate({ title: newTemplateName.trim() }))
            .then(() => dispatch(setNewTemplateName("")))
            .finally(() => setIsCreating(false));
    };

    const handleUpdateTemplate = () => {
        if (!editingTemplate?.title.trim()) {
            message.error(t("template.errors.emptyName"));
            return;
        }
        dispatch(
            RequestUpdateTemplate(editingTemplate.id, {
                title: editingTemplate.title.trim(),
            })
        );
    };

    const handleAddCriterion = (criterionId) => {
        if (!selectedTemplateId) {
            message.warning(t("template.empty"));
            return;
        }
        if (selectedIds.has(criterionId)) {
            message.warning(t("criteria.errors.alreadySelected"));
            return;
        }
        dispatch(RequestAddCriterion(selectedTemplateId, criterionId)).then(() =>
            dispatch(RequestFetchTemplates())
        );
    };

    const handleRemoveCriterion = (criterionId) => {
        if (!selectedTemplateId) return;
        dispatch(RequestRemoveCriterion(selectedTemplateId, criterionId)).then(() =>
            dispatch(RequestFetchTemplates())
        );
    };

    const resetFilters = () => {
        dispatch(setSearchTerm(""));
        dispatch(setFilters({ section: null, creator: null, is_active: null }));
    };

    return (
        <div className="template-composer">
            <Card className="template-switcher">
                <div className="template-switcher__select">
                    <Text strong>{t("template.myTemplates")}</Text>
                    <Select
                        showSearch
                        value={selectedTemplateId}
                        placeholder={t("template.empty")}
                        optionFilterProp="label"
                        onChange={(value) => dispatch(setSelectedTemplate(value))}
                    >
                        {templates.map((tpl) => (
                            <Option key={tpl.id} value={tpl.id} label={tpl.title}>
                                <div className="template-option">
                                    <span>{tpl.title}</span>
                                    <Badge count={tpl.criteria?.length || 0} />
                                </div>
                            </Option>
                        ))}
                    </Select>
                </div>

                <div className="template-switcher__create">
                    <Space.Compact className="template-create">
                        <Input
                            placeholder={t("template.namePlaceholder")}
                            value={newTemplateName}
                            status={templateNameExists && newTemplateName ? "error" : ""}
                            onChange={(event) =>
                                dispatch(setNewTemplateName(event.target.value))
                            }
                            onPressEnter={handleCreateTemplate}
                        />
                        <Tooltip title={t("template.add")}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                loading={isCreating}
                                disabled={!newTemplateName.trim() || templateNameExists}
                                onClick={handleCreateTemplate}
                            />
                        </Tooltip>
                    </Space.Compact>
                </div>

                {selectedTemplate && (
                    <div className="template-switcher__actions">
                        {editingTemplate?.id === selectedTemplate.id ? (
                            <Input
                                value={editingTemplate.title}
                                autoFocus
                                onChange={(event) =>
                                    dispatch(
                                        setEditingTemplate({
                                            ...editingTemplate,
                                            title: event.target.value,
                                        })
                                    )
                                }
                                onPressEnter={handleUpdateTemplate}
                                onBlur={handleUpdateTemplate}
                            />
                        ) : (
                            <>
                                <Tooltip title={t("actions.edit")}>
                                    <Button
                                        icon={<EditOutlined />}
                                        onClick={() => dispatch(setEditingTemplate(selectedTemplate))}
                                    />
                                </Tooltip>
                                <Popconfirm
                                    title={t("template.confirmDelete")}
                                    okText={t("actions.confirm")}
                                    cancelText={t("actions.cancel")}
                                    onConfirm={() =>
                                        dispatch(RequestDeleteTemplate(selectedTemplate.id))
                                    }
                                >
                                    <Tooltip title={t("actions.delete")}>
                                        <Button danger icon={<DeleteOutlined />} />
                                    </Tooltip>
                                </Popconfirm>
                            </>
                        )}
                    </div>
                )}
            </Card>

            <Row gutter={[14, 14]} className="template-composer__grid">
                <Col xs={24} lg={14} className="template-composer__column">
                    <Card className="template-panel template-panel--criteria">
                        <div className="template-panel__head">
                            <Text strong>{t("template.availableCriteria")}</Text>
                            <Badge count={availableCriteria.length} className="template-panel__badge" />
                        </div>

                        <div className="criteria-toolbar">
                            <Input
                                prefix={<SearchOutlined />}
                                placeholder={t("filters.search")}
                                value={searchTerm}
                                allowClear
                                onChange={(event) => dispatch(setSearchTerm(event.target.value))}
                            />
                            <Select
                                placeholder={t("filters.section")}
                                value={filters.section}
                                allowClear
                                onChange={(value) =>
                                    dispatch(setFilters({ ...filters, section: value }))
                                }
                            >
                                {sections.map((section) => (
                                    <Option key={section} value={section}>
                                        {section}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                placeholder={t("filters.creator")}
                                value={filters.creator}
                                allowClear
                                optionLabelProp="label"
                                onChange={(value) =>
                                    dispatch(setFilters({ ...filters, creator: value }))
                                }
                            >
                                {creatorOptions.map((creator) => (
                                    <Option
                                        key={creator.id}
                                        value={creator.id}
                                        label={`${creator.name} ${creator.surname || ""}`.trim()}
                                    >
                                        {creator.name} {creator.surname}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                placeholder={t("filters.status")}
                                value={
                                    filters.is_active !== undefined ? filters.is_active : null
                                }
                                onChange={(value) =>
                                    dispatch(setFilters({ ...filters, is_active: value }))
                                }
                            >
                                <Option value={null}>{t("filters.all")}</Option>
                                <Option value={true}>{t("filters.active")}</Option>
                                <Option value={false}>{t("filters.inactive")}</Option>
                            </Select>
                            <Button onClick={resetFilters}>{t("filters.all")}</Button>
                        </div>

                        <div className="criteria-list">
                            {availableCriteria.length > 0 ? (
                                availableCriteria.map((criterion) => (
                                    <CriterionRow
                                        key={criterion.id}
                                        criterion={criterion}
                                        action="add"
                                        language={language}
                                        onClick={() => handleAddCriterion(criterion.id)}
                                    />
                                ))
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t("criteria.emptyAvailable")}
                                />
                            )}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} lg={10} className="template-composer__column">
                    <Card className="template-panel template-panel--selected">
                        <div className="template-panel__head">
                            <div className="selected-template-title">
                                <Text strong ellipsis={{ tooltip: selectedTemplate?.title }}>
                                    {selectedTemplate?.title || t("template.empty")}
                                </Text>
                                <Text type="secondary">
                                    {selectedCriteria.length} {t("tabs.selected").toLowerCase()}
                                </Text>
                            </div>
                            {selectedTemplate && (
                                <Tag icon={<CheckOutlined />} className="selected-template-tag">
                                    {t("template.activeTemplate")}
                                </Tag>
                            )}
                        </div>

                        <div className="criteria-list criteria-list--selected">
                            {selectedCriteria.length > 0 ? (
                                selectedCriteria.map((criterion) => (
                                    <CriterionRow
                                        key={criterion.id}
                                        criterion={criterion}
                                        action="remove"
                                        language={language}
                                        onClick={() => handleRemoveCriterion(criterion.id)}
                                    />
                                ))
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t("tabs.emptySelected")}
                                />
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

const CriterionRow = ({ criterion, action, language, onClick }) => {
    const { t } = useTranslation("criteriaView");
    const creatorName = `${criterion.creator?.name || ""} ${
        criterion.creator?.surname || ""
    }`.trim();
    const isRemove = action === "remove";
    const text = resolveCriterionText(criterion, language);

    return (
        <button
            type="button"
            className={`criterion-row ${isRemove ? "criterion-row--selected" : ""}`}
            onClick={onClick}
        >
            <span
                className={`criterion-row__status ${
                    criterion.is_active
                        ? "criterion-row__status--active"
                        : "criterion-row__status--inactive"
                }`}
            />
            <span className="criterion-row__content">
                <span className="criterion-row__top">
                    <Text strong ellipsis={{ tooltip: text.title }}>
                        {text.title}
                    </Text>
                    {criterion.section && (
                        <Tag className="criterion-row__section">{criterion.section}</Tag>
                    )}
                </span>
                <span className="criterion-row__meta">
                    {creatorName ? (
                        <span className="criterion-row__creator">
                            <Avatar
                                size={16}
                                src={criterion.creator?.avatar}
                                icon={<UserOutlined />}
                            />
                            <Text type="secondary" ellipsis={{ tooltip: creatorName }}>
                                {creatorName}
                            </Text>
                        </span>
                    ) : (
                        <Text type="secondary">-</Text>
                    )}
                    {text.description && (
                        <Text type="secondary" ellipsis={{ tooltip: text.description }}>
                            {text.description}
                        </Text>
                    )}
                </span>
            </span>
            <Tooltip title={isRemove ? t("actions.remove") : t("actions.add")}>
                <span className={`criterion-row__action ${isRemove ? "is-remove" : ""}`}>
                    {isRemove ? <CloseOutlined /> : <PlusOutlined />}
                </span>
            </Tooltip>
        </button>
    );
};

export default TemplateCritere;
